import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getSignEmoji,
  getSignTrait,
  getDominionStatusLabel,
} from '../ZodiacClashHelpers';

export default function ClashDominion({
  styles,
  isMobile,
  dominionOpen,
  toggleDominion,
  dominion,
  dominionRange,
  handleChangeDominionRange,
  loadDominion,
  dominionLoading,
  dominionError,
  dominionAnimate,
}) {
  const { t } = useTranslation('zodiacClash');
  const [showFullRanking, setShowFullRanking] = useState(false);

  const rankingItems = Array.isArray(dominion?.items)
    ? dominion.items
    : [];

  const visibleRankingItems = showFullRanking
    ? rankingItems
    : rankingItems.slice(0, 3);

  return (
<>
          {/* ================= Dominion (collapsible) ================= */}
          <div style={styles.dominionWrap}>
            <button
              type="button"
              onClick={toggleDominion}
              style={{
                ...styles.dominionBtn,
                ...(dominionOpen ? styles.dominionBtnOpen : null),
              }}
              aria-expanded={dominionOpen}
              aria-controls="zdx-dominion-panel"
            >
              <span style={styles.dominionBtnLeft}>
                <span style={styles.dominionCrown} aria-hidden="true">🏛️</span>
                <span style={styles.dominionBtnTitle}>{t('zodiacDominion')}</span>
                <span style={styles.dominionBtnSub}>
                  {dominion?.totalVotes != null ? t('voteCount', { count: dominion.totalVotes }) : t('globalRanking')}
                </span>
              </span>
              <span style={styles.dominionChevron} aria-hidden="true">{dominionOpen ? '▴' : '▾'}</span>
            </button>

            {dominionOpen && (
              <div id="zdx-dominion-panel" style={styles.dominionPanel}>
                <div style={styles.dominionTopRow}>
                  <div style={styles.dominionMeta}>
                    <div style={styles.dominionMetaTitle}>{t('last')}</div>
                    <div style={styles.dominionPills}>
                      {['7d','30d','all'].map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => handleChangeDominionRange(r)}
                          style={{
                            ...styles.dominionPill,
                            ...(dominionRange === r ? styles.dominionPillActive : null),
                          }}
                        >
                          {r === '7d' ? t('sevenDays') : r === '30d' ? t('thirtyDays') : t('all')}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => loadDominion(dominionRange)}
                    style={styles.dominionRefresh}
                    title={t('refresh')}
                    aria-label={t('refreshDominion')}
                  >
                    ↻
                  </button>
                </div>

                {dominionLoading && (
                  <div style={styles.dominionState}>✨ {t('calculatingDominion')}</div>
                )}

                {!dominionLoading && dominionError && (
                  <div style={styles.dominionState}>⚠️ {dominionError}</div>
                )}

                {!dominionLoading && !dominionError && dominion?.items && (
                  <div className="zdxDominionGrid" style={styles.dominionList}>
                    {visibleRankingItems.map((it, idx) => {
                      const pos = idx + 1; // posición visual 1..12 (evita #6 repetido)
                      const pct = Math.max(0, Math.min(100, Number(it.pct) || 0));
                      const isTop1 = pos === 1;
                      const isTop2 = pos === 2;
                      const isTop3 = pos === 3;


                      return isMobile ? (
                        <div
                          key={it.sign}
                          style={{
                            ...styles.dominionRowMobile,
                            ...(isTop1 ? styles.dominionRowTop1 : null),
                            ...(isTop2 ? styles.dominionRowTop2 : null),
                            ...(isTop3 ? styles.dominionRowTop3 : null),
                          }}
                        >
                          <div style={styles.dominionRowMobileTop}>
                            <div style={{ ...styles.dominionRank, width: 56 }}>
                              <div style={{ ...styles.dominionRankMain, ...(isTop1 ? styles.dominionRankTop1 : null) }}>
                                #{pos}
                                {(isTop1 || isTop2 || isTop3) && (
                                  <span style={{ marginLeft: 6 }} aria-hidden="true">
                                    {isTop1 ? '👑' : isTop2 ? '🥈' : '🟣'}
                                  </span>
                                )}
                              </div>
                              
                            </div>

                            <div className="zdxDominionLeft" style={styles.dominionSignMobile}>
                              <span className="zdxDominionEmoji" style={styles.dominionEmoji} aria-hidden="true">{getSignEmoji(it.sign)}</span>
                              <div className="zdxDominionInfo" style={styles.dominionText}>
                                <div style={styles.dominionLabel}>{it.label}</div>
                                <div style={styles.dominionSubline}>
                                  {getDominionStatusLabel({ pos, pct })} • {getSignTrait(it.sign)}
                                </div>
                              </div>
                            </div>

                            <div className="zdxDominionPercent" style={styles.dominionPct}>{pct}%</div>
                          </div>

                          <div className="zdxDominionBar" style={styles.dominionBarOuter} aria-hidden="true">
                            <div
                              style={{
                                ...styles.dominionBarInner,
                                width: '100%',
                                transform: dominionAnimate ? `scaleX(${pct / 100})` : 'scaleX(0)',
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div
                          key={it.sign}
                          style={{
                            ...styles.dominionRow,
                            ...(isTop1 ? styles.dominionRowTop1 : null),
                            ...(isTop2 ? styles.dominionRowTop2 : null),
                            ...(isTop3 ? styles.dominionRowTop3 : null),
                          }}
                        >
                          <div style={{ ...styles.dominionRank, ...(isTop1 ? styles.dominionRankTop1 : null) }}>
                            <div className="zdxDominionRank" style={styles.dominionRankMain}>#{pos}</div>
                            
                          </div>

                          <div className="zdxDominionLeft" style={styles.dominionSign}>
                            <span className="zdxDominionEmoji" style={styles.dominionEmoji} aria-hidden="true">{getSignEmoji(it.sign)}</span>
                            <div className="zdxDominionInfo" style={styles.dominionText}>
                              <div style={styles.dominionLabel}>{it.label}</div>
                              <div style={styles.dominionSubline}>
                                {getDominionStatusLabel({ pos, pct })} • {getSignTrait(it.sign)}
                              </div>
                            </div>
                            {(isTop1 || isTop2 || isTop3) && (
                              <span
                                style={{
                                  ...styles.dominionTopBadge,
                                  ...(isTop1 ? styles.dominionTopBadge1 : isTop2 ? styles.dominionTopBadge2 : styles.dominionTopBadge3),
                                }}
                                aria-label={isTop1 ? 'Top 1' : isTop2 ? 'Top 2' : 'Top 3'}
                                title={isTop1 ? t('dominant') : isTop2 ? t('influential') : t('rising')}
                              >
                                {isTop1 ? '👑' : isTop2 ? '🥈' : '🟣'}
                              </span>
                            )}
                          </div>

                          <div className="zdxDominionBar" style={styles.dominionBarOuter} aria-hidden="true">
                            <div
                              style={{
                                ...styles.dominionBarInner,
                                width: '100%',
                                transform: dominionAnimate ? `scaleX(${pct / 100})` : 'scaleX(0)',
                              }}
                            />
                          </div>

                          <div className="zdxDominionPercent" style={styles.dominionPct}>{pct}%</div>
                        </div>
                      );
                    })}

                    <div style={styles.dominionFooter}>
                      <span style={{ opacity: 0.9 }}>
                        {dominion?.startDayKey && dominion?.endDayKey
                          ? t('periodRange', { start: dominion.startDayKey, end: dominion.endDayKey })
                          : t('dynamicPeriod')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {rankingItems.length > 3 && (
            <button
              type="button"
              onClick={() =>
                setShowFullRanking((value) => !value)
              }
              style={styles.dominionExpandButton}
              aria-expanded={showFullRanking}
            >
              {showFullRanking
                ? t('viewTopThree')
                : t('viewFullRanking')}
            </button>
          )}

</>
  );
}
